import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, FileText, Plus, Trash2, SlidersHorizontal } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "@/components/ui/sonner";
import { PaginacaoRodape } from "@/components/PaginacaoRodape";
import { usePaginacaoLocal } from "@/hooks/usePaginacaoLocal";
import { useDebounce } from "@/hooks/useDebounce";
import { useApuracaoDetalhe } from "@/features/contabil/hooks/useApuracoes";
import { apuracaoService } from "@/features/contabil/services/apuracaoService";
import type { TipoApuracao } from "@/features/contabil/types/apuracao";

const fmtCur = (v: number) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v);
const fmtDate = (s: string) => new Date(s).toLocaleDateString("pt-BR");

const TRIBUTOS_POR_TIPO: Record<TipoApuracao, string[]> = {
  "icms": ["ICMS"],
  "pis-cofins": ["PIS", "COFINS"],
  "irpj-csll": ["IRPJ", "CSLL"],
};

type NotaApuracao = NonNullable<ReturnType<typeof useApuracaoDetalhe>["data"]>["notas"][number];

/** Notas do período: filtro por tipo/situação, busca por nº/cliente/CFOP e paginação (NFC-e chega a milhares). */
function NotasApuracao({ notas }: { notas: NotaApuracao[] }) {
  const [tipoNota, setTipoNota] = useState("__todas__");
  const [busca, setBusca] = useState("");
  const termo = useDebounce(busca.trim().toLowerCase(), 300);

  const filtradas = useMemo(() => notas.filter((n) =>
    (tipoNota === "__todas__"
      || (tipoNota === "entrada" && n.isEntrada)
      || (tipoNota === "saida" && !n.isEntrada)
      || (tipoNota === "cancelada" && n.isCancelado))
    && (!termo
      || String(n.numeroNota ?? "").includes(termo)
      || String(n.cfop ?? "").includes(termo)
      || (n.nomeRazao ?? "").toLowerCase().includes(termo)),
  ), [notas, tipoNota, termo]);
  const pag = usePaginacaoLocal(filtradas, 50);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Notas que entraram no cálculo</CardTitle>
        <div className="flex flex-wrap gap-3 pt-2">
          <Input
            className="max-w-xs"
            placeholder="Buscar por nº, cliente ou CFOP..."
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
          <Select value={tipoNota} onValueChange={setTipoNota}>
            <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__todas__">Todas</SelectItem>
              <SelectItem value="entrada">Entradas</SelectItem>
              <SelectItem value="saida">Saídas</SelectItem>
              <SelectItem value="cancelada">Canceladas</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tipo</TableHead>
                <TableHead>Nº</TableHead>
                <TableHead>Série</TableHead>
                <TableHead>CFOP</TableHead>
                <TableHead>Emissão</TableHead>
                <TableHead>Cliente</TableHead>
                <TableHead className="text-right">Valor total</TableHead>
                <TableHead className="text-right">Base ICMS</TableHead>
                <TableHead className="text-right">ICMS</TableHead>
                <TableHead className="text-right">PIS</TableHead>
                <TableHead className="text-right">COFINS</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtradas.length === 0 ? (
                <TableRow><TableCell colSpan={12} className="text-center text-muted-foreground py-8">{notas.length === 0 ? "Nenhuma nota no período." : "Nenhuma nota com esses filtros."}</TableCell></TableRow>
              ) : pag.itensPaginados.map((n) => (
                <TableRow key={n.notaEletronicaId} className={n.isCancelado ? "opacity-50" : ""}>
                  <TableCell>
                    <Badge className={n.isEntrada ? "bg-green-500/10 text-green-600" : "bg-blue-500/10 text-blue-600"}>
                      {n.isEntrada ? "Entrada" : "Saída"}
                    </Badge>
                  </TableCell>
                  <TableCell>{n.numeroNota}</TableCell>
                  <TableCell>{n.serie}</TableCell>
                  <TableCell className="font-mono">{n.cfop}</TableCell>
                  <TableCell>{fmtDate(n.dtEmissao)}</TableCell>
                  <TableCell>{n.nomeRazao}</TableCell>
                  <TableCell className="text-right">{fmtCur(n.valorTotal)}</TableCell>
                  <TableCell className="text-right">{fmtCur(n.baseIcms)}</TableCell>
                  <TableCell className="text-right">{fmtCur(n.valorIcms)}</TableCell>
                  <TableCell className="text-right">{fmtCur(n.valorPis)}</TableCell>
                  <TableCell className="text-right">{fmtCur(n.valorCofins)}</TableCell>
                  <TableCell>
                    {n.isCancelado ? (
                      <Badge className="bg-red-500/10 text-red-600">Cancelada</Badge>
                    ) : (
                      <Badge variant="outline"><FileText className="h-3 w-3 mr-1" />OK</Badge>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        {filtradas.length > 0 && (
          <PaginacaoRodape {...pag} onPaginaChange={pag.setPagina} onTamanhoPaginaChange={pag.setTamanhoPagina} />
        )}
      </CardContent>
    </Card>
  );
}

function AjustesApuracao({ tipo, id }: { tipo: TipoApuracao; id: number }) {
  const qc = useQueryClient();
  const tributos = TRIBUTOS_POR_TIPO[tipo] ?? [];
  const [valor, setValor] = useState("");
  const [tributo, setTributo] = useState(tributos[0] ?? "");
  const [descricao, setDescricao] = useState("");

  const { data: ajustes = [], isLoading } = useQuery({
    queryKey: ["apuracao-ajustes", tipo, id],
    queryFn: () => apuracaoService.listarAjustes(tipo, id),
    enabled: id > 0,
  });

  const criar = useMutation({
    mutationFn: () => apuracaoService.criarAjuste(tipo, id, {
      valor: Number(valor.replace(",", ".")),
      tributo: tributo || null,
      descricao: descricao.trim(),
    }),
    onSuccess: () => {
      toast.success("Ajuste lançado.");
      setValor(""); setDescricao("");
      qc.invalidateQueries({ queryKey: ["apuracao-ajustes", tipo, id] });
    },
    onError: (err: unknown) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      toast.error("Erro", { description: (err as any)?.response?.data?.mensagem ?? "Não foi possível lançar o ajuste." });
    },
  });

  const remover = useMutation({
    mutationFn: (ajusteId: number) => apuracaoService.excluirAjuste(tipo, id, ajusteId),
    onSuccess: () => {
      toast.success("Ajuste removido.");
      qc.invalidateQueries({ queryKey: ["apuracao-ajustes", tipo, id] });
    },
    onError: (err: unknown) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      toast.error("Erro", { description: (err as any)?.response?.data?.mensagem ?? "Não foi possível remover." });
    },
  });

  const total = ajustes.reduce((s, a) => s + a.valor, 0);
  const valorNum = Number(valor.replace(",", "."));
  const podeSalvar = !Number.isNaN(valorNum) && valorNum !== 0 && descricao.trim().length > 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <SlidersHorizontal className="h-4 w-4" /> Ajustes manuais
        </CardTitle>
        <CardDescription>
          Lance ajustes ao saldo apurado (estornos, créditos extemporâneos, multas/juros, deduções).
          Valor positivo aumenta o valor a recolher; negativo reduz. Não altera o cálculo automático.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
          {tributos.length > 1 && (
            <div className="md:col-span-2 space-y-1">
              <Label className="text-xs">Tributo</Label>
              <Select value={tributo} onValueChange={setTributo}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {tributos.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="md:col-span-2 space-y-1">
            <Label className="text-xs">Valor (+/-)</Label>
            <Input type="text" inputMode="decimal" placeholder="0,00" value={valor}
              onChange={(e) => setValor(e.target.value)} />
          </div>
          <div className="md:col-span-6 space-y-1">
            <Label className="text-xs">Descrição</Label>
            <Input value={descricao} onChange={(e) => setDescricao(e.target.value)}
              placeholder="Justificativa do ajuste" />
          </div>
          <div className="md:col-span-2">
            <Button className="w-full gap-2" disabled={!podeSalvar || criar.isPending}
              onClick={() => criar.mutate()}>
              <Plus className="h-4 w-4" /> Lançar
            </Button>
          </div>
        </div>

        {isLoading ? <Skeleton className="h-24" /> : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Data</TableHead>
                <TableHead>Tributo</TableHead>
                <TableHead>Descrição</TableHead>
                <TableHead className="text-right">Valor</TableHead>
                <TableHead className="w-12"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {ajustes.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-6">
                  Nenhum ajuste lançado.
                </TableCell></TableRow>
              ) : ajustes.map((a) => (
                <TableRow key={a.id}>
                  <TableCell>{fmtDate(a.dtCriacao)}</TableCell>
                  <TableCell>{a.tributo ?? "—"}</TableCell>
                  <TableCell>{a.descricao}</TableCell>
                  <TableCell className={`text-right font-medium ${a.valor < 0 ? "text-green-600" : "text-red-600"}`}>
                    {fmtCur(a.valor)}
                  </TableCell>
                  <TableCell>
                    <Button variant="ghost" size="icon" disabled={remover.isPending}
                      onClick={() => remover.mutate(a.id)}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {ajustes.length > 0 && (
                <TableRow>
                  <TableCell colSpan={3} className="text-right font-semibold">Total de ajustes</TableCell>
                  <TableCell className={`text-right font-bold ${total < 0 ? "text-green-600" : "text-red-600"}`}>
                    {fmtCur(total)}
                  </TableCell>
                  <TableCell />
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

export default function ApuracaoDetalhe() {
  const { tipo, id } = useParams<{ tipo: TipoApuracao; id: string }>();
  const apuracaoId = Number(id ?? 0);
  const tipoApuracao = (tipo ?? "icms") as TipoApuracao;

  const { data, isLoading } = useApuracaoDetalhe(tipoApuracao, apuracaoId);

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center gap-4">
        <Button asChild variant="ghost" size="sm">
          <Link to="/contabil/apuracoes"><ArrowLeft className="h-4 w-4 mr-2" /> Voltar</Link>
        </Button>
        <div>
          <h1 className="text-2xl font-bold">Detalhe da apuração</h1>
          {data && (
            <p className="text-muted-foreground">
              {data.tipo} — período <span className="font-mono">{data.periodo}</span> —{" "}
              {data.totalNotas} notas no período
            </p>
          )}
        </div>
      </div>

      {isLoading ? <Skeleton className="h-96" /> : !data ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground">Apuração não encontrada.</CardContent></Card>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card><CardHeader><CardTitle className="text-sm text-muted-foreground">Total de notas</CardTitle></CardHeader>
              <CardContent className="text-2xl font-bold">{data.totalNotas}</CardContent>
            </Card>
            <Card><CardHeader><CardTitle className="text-sm text-muted-foreground">Débitos (saídas)</CardTitle></CardHeader>
              <CardContent className="text-2xl font-bold text-red-600">{fmtCur(data.totalDebito)}</CardContent>
            </Card>
            <Card><CardHeader><CardTitle className="text-sm text-muted-foreground">Créditos (entradas)</CardTitle></CardHeader>
              <CardContent className="text-2xl font-bold text-green-600">{fmtCur(data.totalCredito)}</CardContent>
            </Card>
          </div>

          <NotasApuracao notas={data.notas} />

          <AjustesApuracao tipo={tipoApuracao} id={apuracaoId} />
        </>
      )}
    </div>
  );
}
